<template>
    <div class="step3">
        <h1>Aanvraagformulier</h1>
        <h2>Samenstelling huishouden</h2>

        <application-stepper current-step="3" :loading="!loaded">
            <v-form @submit.prevent="submit">
                <v-error-messages ref="errorMessages" :messages="this.errorMessages"></v-error-messages>

                <h3>Volwassenen</h3>
                <p>
                    Hoeveel personen van 18 jaar of ouder behoren naast u zelf tot uw huishouden of gezin?
                </p>

                <v-slider
                    v-model="applicant.householdAdultCount"
                    class="pt-6"
                    label="Aantal"
                    step="1"
                    max="4"
                    thumb-label="always"
                    ticks
                ></v-slider>

                <v-card v-for="adult in householdAdultsShowable" :key="'adult-' + adult.id" class="mx-1 my-3">
                    <v-card-text>
                        <v-radio-group :error="hasErrors('householdAdults',adult.id-1, 'gender')"
                                       :error-messages="handleErrors('householdAdults', adult.id-1, 'gender')" label="Geslacht"
                                       v-model="adult.gender" row>
                            <v-radio key="male" label="man" value="male"></v-radio>
                            <v-radio key="female" label="vrouw" value="female"></v-radio>
                            <v-radio key="other" label="gender neutraal" value="other"></v-radio>
                        </v-radio-group>

                        <v-text-field :error="hasErrors('householdAdults',adult.id-1, 'name')" :error-messages="handleErrors('householdAdults', adult.id-1, 'name')" label="Voornaam en achternaam" v-model="adult.name"></v-text-field>
                      <v-birthday-picker :error="hasErrors('householdAdults',adult.id-1, 'birthdate')" :error-messages="handleErrors('householdAdults', adult.id-1, 'birthdate')"  v-model="adult.birthdate" label="Geboortedatum"></v-birthday-picker>

                    </v-card-text>
                </v-card>

                <h3>Kinderen</h3>
                <p>
                    Hoeveel personen onder de 18 jaar behoren tot uw huishouden of gezin?
                </p>

                <v-slider
                    v-model="applicant.householdChildCount"
                    class="pt-6"
                    label="Aantal"
                    step="1"
                    max="15"
                    thumb-label="always"
                    ticks
                ></v-slider>

                <v-card v-for="child in householdChildrenShowable" :key="'child-' + child.id" class="mx-1 my-3">
                    <v-card-text>
                        <v-radio-group :error="hasErrors('householdChildren',child.id-1, 'gender')" :error-messages="handleErrors('householdChildren', child.id-1, 'gender')" label="Geslacht" v-model="child.gender" row>
                            <v-radio key="male" label="jongen" value="male"></v-radio>
                            <v-radio key="female" label="meisje" value="female"></v-radio>
                            <v-radio key="other" label="gender neutraal" value="other"></v-radio>
                        </v-radio-group>

                        <v-text-field :error="hasErrors('householdChildren',child.id-1, 'name')" :error-messages="handleErrors('householdChildren', child.id-1, 'name')"  label="Voornaam en achternaam" v-model="child.name"></v-text-field>

                        <v-birthday-picker :error="hasErrors('householdChildren',child.id-1, 'birthdate')" :error-messages="handleErrors('householdChildren', child.id-1, 'birthdate')"  v-model="child.birthdate" label="Geboortedatum"></v-birthday-picker>
                    </v-card-text>
                </v-card>

                <v-row>
                    <v-spacer></v-spacer>
                    <v-col cols="auto">
                        <v-btn @click="back" :loading="backBtnClicked" :disabled="submitting">Vorige</v-btn>
                    </v-col>
                    <v-col cols="auto">
                        <v-btn color="primary" @click="submit" :loading="submitBtnClicked" :disabled="submitting"
                        >Volgende
                        </v-btn
                        >
                    </v-col>
                </v-row>
            </v-form>
        </application-stepper>
    </div>
</template>

<script>
import {mapGetters, mapActions} from 'vuex';
import VBirthdayPicker from '@/components/VBirthdayPicker';
import ApplicationStepper from '../components/ApplicationStepper';
import VErrorMessages from '@/components/VErrorMessages';
import HouseholdAdult from '../types/HouseholdAdult';
import HouseholdChild from '../types/HouseholdChild';

export default {
    name: 'step3-household',
    components: {
        ApplicationStepper,
        VErrorMessages,
        VBirthdayPicker,
    },
    data: () => ({
        errorDetails: {},
        loaded: false,
        backBtnClicked: false,
        submitBtnClicked: false,
        submitting: false,

        errorMessages: [],
        applicant: {
            householdAdultCount: 0,
            householdAdults: [],
            householdChildCount: 0,
            householdChildren: [],
        },
    }),
    computed: {
        ...mapGetters({vuex: 'step3'}),
        householdAdultsShowable() {
            return this.applicant.householdAdults.filter(adult => adult.show);
        },
        householdChildrenShowable() {
            return this.applicant.householdChildren.filter(child => child.show);
        },
    },
    created: function () {
        const that = this;

        this.step3Load().then(function () {
            that.applicant.householdAdultCount = that.vuex.applicant.householdAdultCount;
            that.applicant.householdAdults = that.vuex.applicant.householdAdults;
            that.applicant.householdChildCount = that.vuex.applicant.householdChildCount;
            that.applicant.householdChildren = that.vuex.applicant.householdChildren;

            that.loaded = true;
        });
    },
    watch: {
        'applicant.householdAdultCount'(newValue) {
            // add new elements
            while (this.applicant.householdAdults.length < newValue) {
                const newAdult = new HouseholdAdult(this.applicant.householdAdults.length + 1);
                this.applicant.householdAdults.push(newAdult);
            }
            // mark all items hidden
            for (const adult of this.applicant.householdAdults) {
                adult.show = false;
            }
            // mark required items shown
            for (let idx = 0; idx < newValue; idx++) {
                this.applicant.householdAdults[idx].show = true;
            }
        },
        'applicant.householdChildCount'(newValue) {
            // add new elements
            while (this.applicant.householdChildren.length < newValue) {
                const newChild = new HouseholdChild(this.applicant.householdChildren.length + 1);
                this.applicant.householdChildren.push(newChild);
            }
            // mark all items hidden
            for (const child of this.applicant.householdChildren) {
                child.show = false;
            }
            // mark required items shown
            for (let idx = 0; idx < newValue; idx++) {
                this.applicant.householdChildren[idx].show = true;
            }
        },
    },
    methods: {
        ...mapActions(['step3Load', 'step3Submit']),
        hasErrors(name, subkey, entity) {
            return '' !== this.handleErrors(name, subkey, entity);
        },
        handleErrors(name, subkey, entity) {
            if(this.errorDetails[name] === undefined){
                return '';
            }
            let detailObj = this.errorDetails[name];
            if(subkey !== undefined){
                if(detailObj[subkey] === undefined){
                    return '';
                }
                detailObj = detailObj[subkey];
            }
            if(entity !== undefined){
                if(detailObj[entity] === undefined){
                    return '';
                }
                detailObj = detailObj[entity];
            }

            if (this.errorDetails[name] !== undefined) {
                let errorObj = this.errorDetails[name]
                if (entity !== undefined) {
                    errorObj = this.errorDetails[name][subkey][entity]
                } else if (subkey !== undefined) {
                    errorObj = this.errorDetails[name][subkey]
                }
                let error = '';
                Object.entries(errorObj).forEach(entry => {
                    const [errorType, value] = entry;
                    error += " " + value;
                });
                return error;
            }
            return '';
        },
        getPayload: function () {
            const payload = {
                applicant: {
                    householdAdultCount: this.applicant.householdAdultCount,
                    householdAdults: [],
                    householdChildCount: this.applicant.householdChildCount,
                    householdChildren: [],
                },
            };
            payload.applicant.householdAdults = this.householdAdultsShowable.map(function (adult) {
                return {
                    name: adult.name,
                    gender: adult.gender,
                    birthdate: adult.birthdate,
                };
            });
            payload.applicant.householdChildren = this.householdChildrenShowable.map(function (child) {
                return {
                    name: child.name,
                    gender: child.gender,
                    birthdate: child.birthdate,
                };
            });
            return payload;
        },
        back: function () {
            const that = this;
            this.backBtnClicked = true;
            this.submitting = true;
            this.errorMessages = [];

            this.step3Submit(this.getPayload()).finally(function () {
                that.$router.push({name: 'step2_contact'});
                that.backBtnClicked = false;
                that.submitting = false;
            });
        },
        submit: function () {
            const that = this;
            this.submitBtnClicked = true;
            this.submitting = true;
            this.errorMessages = [];
            this.step3Submit(this.getPayload())
                .then(function () {
                    that.$router.push({name: 'step4_capital'});
                })
                .catch(function (error) {
                    const response = error.response;
                    if (response.data !== undefined && response.data.error !== undefined) {
                        that.errorDetails = response.data.error.messages;
                        that.errorMessages = ['Er ontbreken gegevens in deze stap'];
                    } else {
                        that.errorMessages = ['Onbekende fout!'];
                    }
                    that.$vuetify.goTo(that.$refs.errorMessages, {
                        duration: 300,
                        offset: 100,
                        easing: 'easeInOutCubic',
                    });
                })
                .finally(function () {
                    that.submitBtnClicked = false;
                    that.submitting = false;
                });
        },
    },
};
</script>
